/**
 * OS DESENHOS QUE NÃO CABIAM NA GAVETA — T26.R10 (relatório de uso de 09/10/2026).
 *
 * O testador viu «Sua história voltou com você, mas os desenhos não couberam na memória do
 * navegador e ficaram para trás». A gaveta da aba (sessionStorage) cabe ~5 MB, e 8 desenhos em
 * base64 passam disso: quem ia ao login para salvar voltava com a história e SEM os desenhos.
 *
 * Aqui os desenhos vão para o banco do navegador próprio para coisa grande (IndexedDB), com duas
 * travas, porque o motivo de a gaveta ser a da ABA continua valendo — é conteúdo de criança num
 * computador de escola, que é compartilhado:
 *   1. a entrada leva o número desta aba (guardado na gaveta da aba), e só esta aba a lê;
 *   2. ela vence em 2 horas, e toda leitura apaga as vencidas (de qualquer aba).
 */
import type { DocumentoHQ } from "../../api/documento.types";

const BANCO = "hq-lab-rascunho";
const MESA = "desenhos";
const CHAVE_DA_ABA = "hq-lab:numero-da-aba";
export const VALIDADE_MS = 2 * 60 * 60 * 1000;

function numeroDaAba(): string | null {
  try {
    let n = sessionStorage.getItem(CHAVE_DA_ABA);
    if (!n) {
      n = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(CHAVE_DA_ABA, n);
    }
    return n;
  } catch {
    return null;
  }
}

function abrir(): Promise<IDBDatabase | null> {
  return new Promise((resolver) => {
    try {
      if (typeof indexedDB === "undefined") return resolver(null);
      const pedido = indexedDB.open(BANCO, 1);
      pedido.onupgradeneeded = () => pedido.result.createObjectStore(MESA);
      pedido.onsuccess = () => resolver(pedido.result);
      pedido.onerror = () => resolver(null);
    } catch {
      resolver(null);
    }
  });
}

function emTransacao<T>(db: IDBDatabase, modo: IDBTransactionMode, fazer: (m: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return new Promise((resolver) => {
    const t = db.transaction(MESA, modo);
    const r = fazer(t.objectStore(MESA));
    t.oncomplete = () => resolver(r ? (r.result as T) : undefined);
    t.onerror = () => resolver(undefined);
    t.onabort = () => resolver(undefined);
  });
}

/**
 * T29.2 e T29.4 · O QUE O REDESENHAR PRECISA, guardado junto com os desenhos.
 *
 * O relatório de 14/10: «Os botões "Redesenhar o quadro" somem depois de ir ao login e voltar» e
 * «Recarregar durante a geração perde tudo e mostra a HQ anterior como se fosse a atual». As duas
 * têm a mesma causa: só o DOCUMENTO atravessava a saída da tela. O pedido de cada quadro, a
 * semente da história, o estado de cada quadro e as fichas dos personagens ficavam na memória da
 * aba — e é deles que o «Redesenhar» e o «desenhar o que faltou» precisam.
 */
export interface GeracaoGuardada {
  pedidos: { paginaId: string; quadroId: string; prompt: string; cena?: string; quem?: string[] }[];
  semente: number | null;
  quadros: ("esperando" | "desenhando" | "pronto" | "falhou")[];
  fichas: Record<string, string>;
  referencia: { indice: number; img: string } | null;
  estilo?: string;
  /** A geração ainda estava desenhando quando foi guardada (a página recarregou no meio). */
  emAndamento: boolean;
}

export interface HistoriaGuardada {
  documento: DocumentoHQ;
  geracao?: GeracaoGuardada;
}

/** Guarda o documento inteiro, com os desenhos (e o que o redesenhar precisa), para esta aba. Nunca lança. */
export async function guardarOsDesenhos(documento: DocumentoHQ, geracao?: GeracaoGuardada): Promise<boolean> {
  const aba = numeroDaAba();
  const db = aba ? await abrir() : null;
  if (!db || !aba) return false;
  try {
    await emTransacao(db, "readwrite", (m) => m.put({ documento, ...(geracao ? { geracao } : {}), em: Date.now() }, aba));
    return true;
  } catch {
    return false;
  } finally {
    db.close();
  }
}

/**
 * T29.2 · Uma história NOVA começou: a anterior guardada nesta aba sai. Sem isto, recarregar no
 * meio da nova devolvia a velha como se fosse a atual. Nunca lança.
 */
export async function esquecerOsDesenhos(): Promise<void> {
  const aba = numeroDaAba();
  const db = aba ? await abrir() : null;
  if (!db || !aba) return;
  try {
    await emTransacao(db, "readwrite", (m) => m.delete(aba));
  } catch {
    // nada a fazer: o pior caso é a entrada vencer sozinha em 2 h
  } finally {
    db.close();
  }
}

/** Devolve o documento com os desenhos desta aba, se houver e não tiver vencido. */
export async function recuperarOsDesenhos(agora = Date.now()): Promise<DocumentoHQ | null> {
  return (await recuperarAHistoria(agora))?.documento ?? null;
}

/** O documento e, quando houver, o que o redesenhar precisa. */
export async function recuperarAHistoria(agora = Date.now()): Promise<HistoriaGuardada | null> {
  const aba = numeroDaAba();
  const db = aba ? await abrir() : null;
  if (!db || !aba) return null;
  try {
    // Limpeza: tudo o que venceu sai, desta aba ou de qualquer outra.
    const chaves = ((await emTransacao(db, "readonly", (m) => m.getAllKeys())) ?? []) as IDBValidKey[];
    const lidos = await Promise.all(chaves.map((k) => emTransacao<{ em: number }>(db, "readonly", (m) => m.get(k))));
    const vencidas = chaves.filter((_, i) => !lidos[i] || agora - (lidos[i]?.em ?? 0) > VALIDADE_MS);
    const daAba = (await emTransacao<{ documento: DocumentoHQ; geracao?: GeracaoGuardada; em: number }>(db, "readonly", (m) => m.get(aba))) ?? null;
    // A entrada DESTA aba não é apagada na leitura: medido em 13/10, a volta do login lia e
    // apagava no mesmo instante em que a tela a regravava, e na segunda ida ao login os desenhos
    // já não estavam lá. Ela é substituída a cada gravação e vence em 2 h; só as vencidas saem.
    await emTransacao(db, "readwrite", (m) => {
      for (const k of vencidas) m.delete(k);
    });
    if (!daAba || agora - daAba.em > VALIDADE_MS) return null;
    return { documento: daAba.documento, ...(daAba.geracao ? { geracao: daAba.geracao } : {}) };
  } catch {
    return null;
  } finally {
    db.close();
  }
}
